// Charge category humanizer.
//
// Categories are stored as free-form strings (typeahead with allowCreate).
// Legacy data — and rows imported from QB before the schema widened — use
// the old enum-style codes ('special', 'late_fee', etc.). This converts
// either form to a label suitable for display in the ledger.

import { ChargeCategory } from '@/types/models';

const LEGACY_LABELS: Record<string, string> = {
	assessment: 'Dues',
	late_fee: 'Late Fee',
	special: 'Water',
	other: 'Other',
	adjustment: 'Adjustment',
	legal_fees: 'Legal Fees',
	administration: 'Administration',
	clubhouse: 'Clubhouse',
};

export const formatChargeCategory = (raw: string | undefined | null): string => {
	if (!raw) return '';
	const key = raw.toLowerCase();
	if (LEGACY_LABELS[key]) return LEGACY_LABELS[key];
	// Already a free-form label — return as-is.
	return raw;
};

// Stable check that doesn't care whether category is the legacy enum or a
// humanized label. Used to detect late-fee charges for the editor handoff.
export const isLateFeeCategory = (raw: string | undefined | null): boolean => {
	if (!raw) return false;
	const key = raw.toLowerCase().replace(/\s+/g, '_');
	return key === 'late_fee';
};

/** `items.item_code` of the global late-fee item the late-fee cron posts with. */
export const LATE_FEE_ITEM_CODE = 'LATE FEE';

/**
 * Category for an invoice line after its Item changes. A line billed with the LATE FEE
 * item is a late fee: tag it `late_fee`, as the cron does, so the late-fee run doesn't
 * treat it as an overdue charge that triggers another fee. Moving off that item drops
 * the tag again; any other category is left alone.
 */
export const chargeCategoryForItem = (
	item: { itemCode?: string } | null,
	current: ChargeCategory,
): ChargeCategory => {
	if (item?.itemCode === LATE_FEE_ITEM_CODE) return ChargeCategory.LateFee;
	return current === ChargeCategory.LateFee ? ChargeCategory.Assessment : current;
};
